// @flow

import React, { useState, type Element } from 'react'
import {
  Button,
  Container,
  Grid,
  Typography,
  Box,
  Snackbar,
  TextField,
  CircularProgress,
} from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import { makeStyles } from '@material-ui/core/styles'

import AlertError from '../../Common/AlertError'

function Alert(props) {
  return <MuiAlert elevation={0} variant="filled" {...props} />
}

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiAlert-icon': {
      fontSize: 20,
    },
    '& .MuiAlert-message': {
      padding: '12px 0',
    },
    '& .MuiInputBase-input': {
      textTransform: 'uppercase',
    },
  },
  body1: {
    fontSize: 16,
    color: '#333333',
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  containerPadding: {
    padding: '0 0',
  },
}))

type Props = {
  email: string,
  registrationUuid: string,
  storeActivationCode: (string) => void,
  goNextPageCallback: () => void,
}

function EmailConfirmation({
  email,
  registrationUuid,
  storeActivationCode,
  goNextPageCallback,
}: Props): Element<'div'> {
  const classes = useStyles()
  const [activationCode, setActivationCode] = useState('')
  const [isActivationCodeInvalid, showActivationCodeError] = useState(false)
  const [isShowingResendCodeMsg, showResendCodeMsg] = useState(false)
  const [isSubmitLoading, showSubmitButtonLoading] = React.useState(false)
  const [isResendCodeLoading, showResendCodeButtonLoading] = React.useState(false)
  const [serverErrors, setServerErrors] = useState(null)

  const handleActivationCodeFieldChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
    const value = event.target.value

    showActivationCodeError(false)

    setActivationCode(value)
  }

  const handleCloseAlertResendCode = (event, reason): void => {
    showResendCodeMsg(false)
  }

  async function handleResendCode(event: SyntheticEvent<HTMLButtonElement>): Promise<void> {
    showResendCodeButtonLoading(true)
    await requestResendCode()
    showResendCodeButtonLoading(false)
  }

  async function requestResendCode(): Promise<void> {
    const payload = { registration_uuid: registrationUuid }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }

    const path = '/api/v4/sign_up/resend_activation_code'

    try {
      const response = await fetch(path, requestOptions)

      if (response.status === 201) {
        showResendCodeMsg(true)
      } else {
        setServerErrors({ non_field_errors: ['Something went wrong.'] })
      }
    } catch (err) {
      setServerErrors({ non_field_errors: ['Something went wrong.'] })
    }
  }

  async function handleSubmit(event: SyntheticEvent<HTMLButtonElement>): Promise<void> {
    showSubmitButtonLoading(true)
    const success = await requestSubmit()
    showSubmitButtonLoading(false)

    if (success) {
      goNextPageCallback()
    }
  }

  async function requestSubmit(): Promise<boolean> {
    const payload = {
      registration_uuid: registrationUuid,
      activation_code: activationCode,
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }

    const path = '/api/v4/sign_up/email_confirmation'

    try {
      const response = await fetch(path, requestOptions)

      if (response.status === 200) {
        storeActivationCode(activationCode)

        return Promise.resolve(true)
      }

      if (response.status === 404) {
        showActivationCodeError(true)
      } else {
        setServerErrors({ non_field_errors: ['Something went wrong.'] })
      }
    } catch (err) {
      setServerErrors({ non_field_errors: ['Something went wrong.'] })
    }

    return Promise.resolve(false)
  }

  return (
    <Container className={classes.containerPadding} maxWidth="sm">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h3">Please check your email</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography component={'span'} className={classes.body1}>
            <Box display="inline">Enter the verification code we sent to </Box>
            <Box display="inline" fontWeight="bold">
              {email}
            </Box>
            <Box display="inline"> to confirm this email belongs to you.</Box>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            className={classes.root}
            id="verification-code"
            label="Verification code"
            variant="outlined"
            value={activationCode}
            onChange={handleActivationCodeFieldChange}
            inputProps={{ maxLength: 6 }}
            error={isActivationCodeInvalid}
            helperText={isActivationCodeInvalid ? 'Verification code is invalid' : ''}
            fullWidth
            required
          />
        </Grid>
        <Grid container item spacing={2} direction="row" justify="flex-end" alignItems="center">
          <Grid
            component={Box}
            item
            style={{ padding: '0px' }}
            xs={12}
            sm="auto"
            display={{ xs: 'block', sm: 'none' }}
          >
            <div className={classes.wrapper}>
              <Button
                variant="contained"
                color="primary"
                disabled={activationCode.length < 6 || isSubmitLoading}
                onClick={handleSubmit}
                data-testid="continueButton"
                fullWidth
              >
                CONTINUE
              </Button>
              {isSubmitLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
            </div>
          </Grid>
          <Grid item style={{ padding: '0px' }} xs={12} sm="auto">
            <div className={classes.wrapper}>
              <Button
                color="primary"
                disabled={isResendCodeLoading}
                onClick={handleResendCode}
                fullWidth
              >
                RESEND CODE
              </Button>
              {isResendCodeLoading && (
                <CircularProgress size={24} className={classes.buttonProgress} />
              )}
            </div>
          </Grid>
          <Grid
            component={Box}
            item
            style={{ padding: '0px' }}
            xs={12}
            sm="auto"
            display={{ xs: 'none', sm: 'block' }}
          >
            <div className={classes.wrapper}>
              <Button
                variant="contained"
                color="primary"
                disabled={activationCode.length < 6 || isSubmitLoading}
                onClick={handleSubmit}
                fullWidth
              >
                SUBMIT
              </Button>
              {isSubmitLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
            </div>
          </Grid>
        </Grid>
        {isShowingResendCodeMsg && (
          <Grid item xs={12}>
            <Box py={1.5} fontWeight="fontWeightMedium" width={1}>
              <Snackbar
                onClose={handleCloseAlertResendCode}
                open={isShowingResendCodeMsg}
                style={{ display: 'block', position: 'inherit', transform: 'none' }}
                autoHideDuration={6000}
              >
                <Alert
                  className={`${classes.root}`}
                  severity="success"
                  onClose={handleCloseAlertResendCode}
                >
                  <Typography color="inherit" variant="body2">
                    A new code has been sent to your email
                  </Typography>
                </Alert>
              </Snackbar>
            </Box>
          </Grid>
        )}

        {serverErrors && <AlertError errors={serverErrors} />}
      </Grid>
    </Container>
  )
}

export default EmailConfirmation
